import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, ExternalLink, Eye, Plus, RotateCw, Save, Search, Trash2, X } from 'lucide-react'
import {
  createTermWatch,
  deleteTermWatch,
  fetchTermWatches,
  fetchTermWatchScanResults,
  fetchTermWatchScans,
  runTermWatchNow,
  sendTermWatchTestEmail,
  toggleTermWatch,
  type TermWatchDto,
} from '../services/api'
import { EmptyState, KpiCard, MimCard, MimPage, StatusPill, relativeAge } from '../components/MimUi'

const schedulePresets = [
  { label: 'Daily', cron: '0 2 * * *' },
  { label: 'Weekly', cron: '0 2 * * 1' },
  { label: 'Monthly', cron: '0 2 1 * *' },
]

export default function TermWatches() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<TermWatchDto | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({
    term: '',
    countryCode: 'GB',
    scheduleIndex: 1,
    notifyEmail: '',
    notes: '',
  })

  const { data: watches = [], isLoading } = useQuery({ queryKey: ['term-watches'], queryFn: fetchTermWatches })
  const { data: scans = [] } = useQuery({
    queryKey: ['term-watch-scans', selected?.id, 'term-watcher-page'],
    queryFn: () => fetchTermWatchScans(selected!.id),
    enabled: !!selected,
  })
  const latestScan = scans[0]
  const { data: selectedResults = [] } = useQuery({
    queryKey: ['term-watch-results', selected?.id, latestScan?.id, 'term-watcher-page'],
    queryFn: () => fetchTermWatchScanResults(selected!.id, latestScan!.id),
    enabled: !!selected && !!latestScan,
  })

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase()
    return watches.filter((watch) => !text || watch.term.toLowerCase().includes(text) || watch.notifyEmail.toLowerCase().includes(text))
  }, [watches, search])

  const active = watches.filter((watch) => watch.isActive).length
  const alertsToday = scans.reduce((sum, scan) => sum + scan.totalResults, 0)

  const createMutation = useMutation({
    mutationFn: () => {
      const preset = schedulePresets[form.scheduleIndex]
      return createTermWatch({
        term: form.term.trim(),
        countryCode: form.countryCode.trim() || 'GB',
        cronExpression: preset.cron,
        scheduleLabel: preset.label,
        notifyEmail: form.notifyEmail.trim(),
        notes: form.notes.trim() || undefined,
      })
    },
    onSuccess: () => {
      setForm({ term: '', countryCode: 'GB', scheduleIndex: 1, notifyEmail: '', notes: '' })
      setShowAdd(false)
      qc.invalidateQueries({ queryKey: ['term-watches'] })
    },
  })

  const toggleMutation = useMutation({
    mutationFn: toggleTermWatch,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['term-watches'] }),
  })

  const runMutation = useMutation({
    mutationFn: runTermWatchNow,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['term-watch-scans'] })
      qc.invalidateQueries({ queryKey: ['term-watches'] })
    },
  })

  const [testEmailMessage, setTestEmailMessage] = useState<string | null>(null)
  const testEmailMutation = useMutation({
    mutationFn: sendTermWatchTestEmail,
    onSuccess: (res) => setTestEmailMessage(res.message),
    onError: (err: any) =>
      setTestEmailMessage(err?.response?.data?.message || err?.message || 'Test email failed (check ACS config).'),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteTermWatch,
    onSuccess: (_, id) => {
      if (selected?.id === id) setSelected(null)
      qc.invalidateQueries({ queryKey: ['term-watches'] })
    },
  })

  return (
    <MimPage title="Term Watcher" subtitle="Monitor registered terms across the internet">
      <div className="mim-stack">
        <div className="mim-grid-4">
          <KpiCard label="Watched Terms" value={watches.length} />
          <KpiCard label="Active Monitors" value={active} tone="green" />
          <KpiCard label="Alerts Today" value={alertsToday} tone="orange" />
          <KpiCard label="Sites Monitored" value={selectedResults.length || '-'} />
        </div>

        <div className="mim-grid-2">
          <div className="mim-stack">
            <div className="mim-toolbar">
              <div className="mim-search">
                <Search size={15} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search terms..." />
              </div>
              <button type="button" className="mim-btn mim-btn-primary" onClick={() => setShowAdd((current) => !current)}>
                <Plus size={14} /> Add Term
              </button>
            </div>

            {showAdd && (
              <MimCard title="Register New Watched Term" className="rules-add-card">
                <div className="mim-grid-2">
                  <label>
                    <span className="mim-label">Term / Phrase *</span>
                    <input className="mim-input" value={form.term} onChange={(e) => setForm((current) => ({ ...current, term: e.target.value }))} placeholder="e.g. Kooltherm K8" />
                  </label>
                  <label>
                    <span className="mim-label">Country</span>
                    <input className="mim-input" value={form.countryCode} onChange={(e) => setForm((current) => ({ ...current, countryCode: e.target.value }))} />
                  </label>
                  <label>
                    <span className="mim-label">Schedule</span>
                    <select className="mim-select" value={form.scheduleIndex} onChange={(e) => setForm((current) => ({ ...current, scheduleIndex: Number(e.target.value) }))}>
                      {schedulePresets.map((preset, index) => <option key={preset.label} value={index}>{preset.label}</option>)}
                    </select>
                  </label>
                  <label>
                    <span className="mim-label">Notification Email *</span>
                    <input className="mim-input" type="email" value={form.notifyEmail} onChange={(e) => setForm((current) => ({ ...current, notifyEmail: e.target.value }))} />
                  </label>
                </div>
                <label style={{ marginTop: 12 }}>
                  <span className="mim-label">Description / Purpose</span>
                  <input className="mim-input" value={form.notes} onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))} />
                </label>
                <div className="mim-toolbar" style={{ marginTop: 12 }}>
                  <button type="button" className="mim-btn mim-btn-primary" disabled={!form.term.trim() || !form.notifyEmail.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>
                    <Save size={14} /> Save Term
                  </button>
                  <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                </div>
              </MimCard>
            )}

            <div className="mim-stack">
              {isLoading && <EmptyState title="Loading watched terms" />}
              {!isLoading && filtered.length === 0 && <EmptyState title="No watched terms found" />}
              {filtered.map((watch) => (
                <article
                  key={watch.id}
                  className={`term-watch-card ${selected?.id === watch.id ? 'active' : ''} ${!watch.isActive ? 'disabled' : ''}`}
                  onClick={() => setSelected(watch)}
                >
                  <div>
                    <div className="term-watch-title">
                      <Eye size={14} />
                      <strong>"{watch.term}"</strong>
                      <StatusPill status={watch.isActive ? 'Active' : 'Paused'} tone={watch.isActive ? 'pass' : 'pending'} />
                    </div>
                    <p>{watch.notifyEmail} | {watch.countryCode} | Last: {relativeAge(watch.lastScanUtc)}</p>
                  </div>
                  <div className="term-watch-actions">
                    <button type="button" className={`mim-switch ${watch.isActive ? 'on' : ''}`} onClick={(e) => { e.stopPropagation(); toggleMutation.mutate(watch.id) }}>
                      <span />
                    </button>
                    <button type="button" className="mim-icon-button" onClick={(e) => { e.stopPropagation(); runMutation.mutate(watch.id) }}>
                      <RotateCw size={14} />
                    </button>
                    <button type="button" className="mim-icon-button" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(watch.id) }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="mim-stack">
            <MimCard
              title={selected ? `Alerts for "${selected.term}"` : 'All Recent Alerts'}
              actions={selected && (
                <div className="mim-toolbar">
                  <button
                    type="button"
                    className="mim-btn mim-btn-secondary"
                    disabled={testEmailMutation.isPending}
                    onClick={() => {
                      setTestEmailMessage(null)
                      testEmailMutation.mutate(selected.id)
                    }}
                  >
                    <Bell size={13} /> {testEmailMutation.isPending ? 'Sending…' : 'Test email'}
                  </button>
                  <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setSelected(null)}><X size={13} /> Clear filter</button>
                </div>
              )}
            >
              {testEmailMessage && <div className="mim-note">{testEmailMessage}</div>}
              {!selected ? (
                <EmptyState title="Select a watched term" detail="Choose a term to view recent scan results and alert context." />
              ) : selectedResults.length === 0 ? (
                <EmptyState title="No alerts for this term" detail="Run a scan to populate recent alerts." />
              ) : (
                <div className="mim-stack">
                  {selectedResults.slice(0, 10).map((result) => (
                    <div className="term-alert-card" key={result.id}>
                      <div className="term-alert-head">
                        <strong>"{selected.term}"</strong>
                        <StatusPill status="Info" tone="info" />
                        <StatusPill status="Notified" tone="pass" />
                        <span>{relativeAge(result.capturedUtc)}</span>
                      </div>
                      <p>{result.title || result.domain || result.url}</p>
                      {result.snippet && <blockquote>"{result.snippet}"</blockquote>}
                      <a href={result.url} target="_blank" rel="noreferrer"><ExternalLink size={12} /> {result.url}</a>
                    </div>
                  ))}
                </div>
              )}
            </MimCard>

            <div className="term-info-card">
              <Bell size={15} />
              <span>Registered terms are monitored on schedule. When terms are detected, the owner receives context and URL details by email.</span>
            </div>
          </div>
        </div>
      </div>
    </MimPage>
  )
}
